import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useProfileCompletion } from '../hooks/useProfileCompletion';
import { CSpinner, CAlert, CButton } from '@coreui/react';
import { CIcon } from '@coreui/icons-react';
import { cilCheckCircle, cilWarning, cilReload } from '@coreui/icons';

/**
 * Profile Completion Guard Component
 * Redirects users to profile page if their profile is incomplete
 * 
 * @param {Object} props
 * @param {React.Component} props.children - The component to render if profile is complete
 * @param {number} props.requiredThreshold - Minimum completion percentage (default: 100)
 * @param {boolean} props.showProgress - Whether to show progress indicator (default: true)
 * @returns {React.Component}
 */
const ProfileCompletionGuard = ({ 
  children, 
  requiredThreshold = 100, 
  showProgress = true 
}) => {
  const location = useLocation();
  const {
    isLoading,
    error,
    percentage,
    meetsThreshold,
    shouldRedirectToProfile,
    nextActions,
    refreshCompletion
  } = useProfileCompletion(requiredThreshold);

  // Don't guard the profile page itself
  if (location.pathname === '/members/profile' || location.pathname === '/members/my-profile') {
    return children;
  }

  // Show loading state
  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
        <div className="text-center">
          <CSpinner color="primary" size="lg" />
          <p className="text-muted mt-3">Checking profile completion...</p>
        </div>
      </div>
    );
  }

  // Show error state with retry option
  if (error) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
        <div className="text-center" style={{ maxWidth: '400px' }}>
          <CAlert color="danger">
            <CIcon icon={cilWarning} className="me-2" />
            <strong>Error loading profile data</strong>
            <p className="mb-3 mt-2">{error}</p>
            <CButton 
              color="primary" 
              size="sm"
              onClick={refreshCompletion}
            >
              <CIcon icon={cilReload} className="me-1" />
              Retry
            </CButton>
          </CAlert>
        </div>
      </div>
    );
  }

  // Redirect to profile if completion threshold not met
  if (shouldRedirectToProfile) {
    return (
      <Navigate 
        to="/members/profile" 
        state={{ 
          from: location,
          reason: 'profile_incomplete',
          percentage,
          nextActions: nextActions.slice(0, 3) // Show top 3 actions
        }} 
        replace 
      />
    );
  }

  // Render children if profile is complete
  return children;
};

/**
 * Profile Completion Progress Component
 * Shows a progress bar and completion status
 */
export const ProfileCompletionProgress = ({ 
  className = '', 
  showDetails = false,
  size = 'default' 
}) => {
  const {
    isLoading,
    percentage,
    isComplete,
    nextActions,
    completedSections,
    missingSections
  } = useProfileCompletion();

  if (isLoading) {
    return (
      <div className={`d-flex align-items-center ${className}`}>
        <CSpinner size="sm" className="me-2" />
        <span className="text-muted">Loading...</span>
      </div>
    );
  }

  const getProgressColor = () => {
    if (percentage >= 100) return 'success';
    if (percentage >= 75) return 'warning';
    if (percentage >= 50) return 'info';
    return 'danger';
  };

  const progressBarStyle = {
    height: size === 'small' ? '8px' : size === 'large' ? '20px' : '12px'
  };

  return (
    <div className={className}>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <small className="text-muted">Profile Completion</small>
        <small className="fw-bold">
          <CIcon 
            icon={cilCheckCircle} 
            className={`me-1 ${isComplete ? 'text-success' : 'text-muted'}`} 
          />
          {percentage}%
        </small>
      </div>
      
      <div className="progress" style={progressBarStyle}>
        <div
          className={`progress-bar bg-${getProgressColor()}`}
          role="progressbar"
          style={{ width: `${percentage}%` }}
          aria-valuenow={percentage}
          aria-valuemin="0"
          aria-valuemax="100"
        />
      </div>

      {showDetails && !isComplete && nextActions.length > 0 && (
        <div className="mt-2">
          <small className="text-muted d-block">Next steps:</small>
          <ul className="list-unstyled mb-0" style={{ fontSize: '0.75rem' }}>
            {nextActions.slice(0, 3).map((action, index) => (
              <li key={index} className="text-muted">
                • {action.fieldLabel}
              </li>
            ))}
            {nextActions.length > 3 && (
              <li className="text-muted">
                • And {nextActions.length - 3} more...
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

/**
 * Profile Completion Banner Component
 * Shows a dismissible banner with completion status and actions
 */
export const ProfileCompletionBanner = ({ onDismiss }) => {
  const {
    isLoading,
    percentage,
    isComplete,
    nextActions,
    shouldRedirectToProfile
  } = useProfileCompletion();

  // Don't show if loading or complete
  if (isLoading || isComplete) {
    return null;
  }

  return (
    <CAlert 
      color={percentage >= 50 ? 'warning' : 'danger'} 
      dismissible={!!onDismiss}
      onClose={onDismiss}
      className="mb-4"
    >
      <div className="d-flex justify-content-between align-items-start">
        <div className="flex-grow-1">
          <h6 className="alert-heading">
            <CIcon icon={cilWarning} className="me-2" />
            Complete Your Profile ({percentage}%)
          </h6>
          <p className="mb-2">
            Your profile is {percentage}% complete. Complete all required fields and upload necessary documents to access all member features.
          </p>
          {nextActions.length > 0 && (
            <div>
              <small className="fw-bold">Next steps:</small>
              <ul className="mb-2 mt-1" style={{ fontSize: '0.875rem' }}>
                {nextActions.slice(0, 3).map((action, index) => (
                  <li key={index}>{action.fieldLabel}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <div className="ms-3">
          <CButton 
            color="primary" 
            size="sm"
            href="/members/profile"
          >
            Complete Profile
          </CButton>
        </div>
      </div>
    </CAlert>
  );
};

export default ProfileCompletionGuard; 